import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyProductKey,
  ShopifyProductToSellingPlanGroupKey,
  ShopifyProductVariantKey,
  ShopifyProductVariantToSellingPlanGroupKey,
  ShopifySellingPlanGroupKey,
  ShopifySellingPlanGroupProductKey,
  ShopifySellingPlanGroupProductToProductKey,
  ShopifySellingPlanGroupProductToSellingPlanGroupKey,
  ShopifySellingPlanGroupProductVariantKey,
  ShopifySellingPlanGroupProductVariantToProductVariantKey,
  ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
  ShopifySellingPlanGroupToProductKey,
  ShopifySellingPlanGroupToProductVariantKey,
  ShopifySellingPlanGroupToShopKey,
  ShopifySellingPlanKey,
  ShopifySellingPlanToSellingPlanGroupKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import { ShopifyApiVersion } from "./ShopifyApiVersion";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySellingPlanGroupKey,
  "2022-01": {
    name: "Shopify Selling Plan Group",
    graphQLTypename: "SellingPlanGroup",
    fields: [
      {
        name: "App ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "appId" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "description" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Merchant Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "merchantCode" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "name" } },
      },
      {
        name: "Options",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "options" } },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "position" } },
        configuration: {
          decimals: 0,
        },
      },
      {
        name: "Summary",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "summary" } },
      },
      {
        name: "Product Variants",
        fieldType: FieldType.HasManyThrough,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "productVariants" } },
        key: ShopifySellingPlanGroupToProductVariantKey,
        configuration: {
          joinModelKey: ShopifySellingPlanGroupProductVariantKey,
          relatedModelKey: ShopifyProductVariantKey,
          inverseJoinModelFieldKey: ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
          inverseRelatedModelFieldKey: ShopifySellingPlanGroupProductVariantToProductVariantKey,
          inverseFieldKey: ShopifyProductVariantToSellingPlanGroupKey,
        },
      },
      {
        name: "Selling Plan Group Product Variants",
        fieldType: FieldType.HasMany,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySellingPlanGroupProductVariantKey,
          inverseFieldKey: ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
        },
      },
      {
        name: "Products",
        fieldType: FieldType.HasManyThrough,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "products" } },
        key: ShopifySellingPlanGroupToProductKey,
        configuration: {
          joinModelKey: ShopifySellingPlanGroupProductKey,
          relatedModelKey: ShopifyProductKey,
          inverseJoinModelFieldKey: ShopifySellingPlanGroupProductToSellingPlanGroupKey,
          inverseRelatedModelFieldKey: ShopifySellingPlanGroupProductToProductKey,
          inverseFieldKey: ShopifyProductToSellingPlanGroupKey,
        },
      },
      {
        name: "Selling Plan Group Products",
        fieldType: FieldType.HasMany,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySellingPlanGroupProductKey,
          inverseFieldKey: ShopifySellingPlanGroupProductToSellingPlanGroupKey,
        },
      },
      {
        name: "Selling Plans",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "sellingPlans" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySellingPlanKey,
          inverseFieldKey: ShopifySellingPlanToSellingPlanGroupKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySellingPlanGroupToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "sellingPlanGroups",
    },
  },
};

export const ShopifySellingPlanGroup = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) => buildConnectionSpec(specs, apiVersion);
